import {
  updateLaneStatus as updateLaneStatusOp,
  updateLaneStatusReady as updateLaneStatusReadyOp,
} from '@gql/operations'
import { getLogger, type GqlExecutor } from '@hopdrive/sdk-core'
import type { LaneStatus } from '../../types'

const VALID_STATUSES: LaneStatus[] = ['new', 'pending', 'enriching', 'ready', 'failed', 'stale']

export interface UpdateLaneStatusParams {
  /** Lane ID or array of lane IDs to update */
  laneId: number | number[]
  /** Target status */
  status: LaneStatus
  /** Optional executor override */
  executor?: GqlExecutor
}

export interface UpdateLaneStatusResult {
  /** Number of lanes updated */
  affectedRows: number
  /** Updated lane records */
  updated: Array<{
    id: number
    status: LaneStatus
    updatedat: string
    /** Present when status is 'ready' */
    enrichedAt?: string
  }>
}

/**
 * Update the status of one or more lanes.
 *
 * When setting status to 'ready', this function automatically sets `enriched_at`
 * to the current timestamp to mark when enrichment completed.
 *
 * @example
 * // Set a single lane to ready (automatically sets enriched_at)
 * const result = await updateLaneStatus({ laneId: 123, status: 'ready' })
 *
 * @example
 * // Set multiple lanes to ready
 * const result = await updateLaneStatus({ laneId: [123, 456], status: 'ready' })
 *
 * @example
 * // Set lane to other statuses (does not affect enriched_at)
 * const result = await updateLaneStatus({ laneId: 123, status: 'failed' })
 */
export async function updateLaneStatus(
  params: UpdateLaneStatusParams
): Promise<UpdateLaneStatusResult> {
  const { laneId, status } = params

  if (!VALID_STATUSES.includes(status)) {
    throw new Error(`Invalid lane status: ${status}. Must be one of: ${VALID_STATUSES.join(', ')}`)
  }

  const ids = Array.isArray(laneId) ? laneId : [laneId]

  if (ids.length === 0) {
    return { affectedRows: 0, updated: [] }
  }

  // When setting to 'ready', use the special mutation that also sets enriched_at
  if (status === 'ready') {
    const enrichedAt = new Date().toISOString()
    const updateLanes = await updateLaneStatusReadyOp(
      { ids, enrichedAt },
      { executor: params.executor }
    )

    const updated = (updateLanes?.returning ?? []).map((lane) => ({
      id: lane.id,
      status: lane.status as LaneStatus,
      updatedat: lane.updatedat as string,
      enrichedAt: lane.enriched_at as string,
    }))

    getLogger().debug('laneStatus.updated', { laneId, status })

    return {
      affectedRows: updateLanes?.affected_rows ?? 0,
      updated,
    }
  }

  // For other statuses, use the standard mutation
  const updateLanes = await updateLaneStatusOp(
    { ids, status },
    { executor: params.executor }
  )

  const updated = (updateLanes?.returning ?? []).map((lane) => ({
    id: lane.id,
    status: lane.status as LaneStatus,
    updatedat: lane.updatedat as string,
  }))

  getLogger().debug('laneStatus.updated', { laneId, status })

  return {
    affectedRows: updateLanes?.affected_rows ?? 0,
    updated,
  }
}
